import React from 'react';
import { useState, useContext, createContext, ComponentProps, useEffect } from 'react';
import { UserInterface } from '../interfaces';
import { ADMIN_TOKEN_STORAGE_KEY, reinitializeAxiosInstance } from '../libraries/axios';
import feathersClient from '../feathers';

export interface AuthContextState {
	user: UserInterface | null
	loginWithEmail: (email: string, password: string) => Promise<void>
	logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextState>({} as AuthContextState);

export const AuthProvider = (props: ComponentProps<any>) => {
  const { children } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [user, setUser] = useState<UserInterface | null>(null);

  // login with email
  const loginWithEmail = async (email: string, password: string) => {
    await feathersClient.authenticate({
      strategy: 'local',
      email,
      password
    });

    const { accessToken, user } = await feathersClient.get('authentication');

    window.localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, accessToken);
    setUser(user);
    reinitializeAxiosInstance(accessToken);
  };

  // logout
  const logout = async () => {
    await feathersClient.logout();
    reinitializeAxiosInstance('');
    window.localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    setUser(null);
    window.localStorage.clear();
  };

  // reauthenticate using stored token
  const initialize = async () => {
    try {
      setIsLoading(true);
      const token = window.localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
      if (token) {
        await feathersClient.reAuthenticate();
        const { accessToken, user } = await feathersClient.get('authentication');
        window.localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, accessToken);
        setUser(user);
        reinitializeAxiosInstance(accessToken);
      }
    } catch {
      setUser(null);
      window.localStorage.clear();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loginWithEmail,
        logout
      }}>
      {(() => {
        if (isLoading) {
          return (
            <div className='h-screen w-screen'>
							Loading...
            </div>
          );
        }

        return children;
      })()}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextState => useContext(AuthContext);
